import { FunctionComponent, ReactNode, useMemo } from 'react'
import { css } from '@emotion/css'
import styled from '@emotion/styled'
import Big from 'big.js'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'

import Map from '@/components/Map'
import { ChargeType, Venue } from '@/libs/helpers/adapters'
import {
  DiscountDetails,
  Order,
  OrderItemDiscount,
  PaymentMethod,
  ServiceChargeType,
} from '@/libs/helpers/apiClient'
import { buildAssetsPath } from '@/libs/helpers/assets'
import { formatPhone } from '@/libs/helpers/formatters'
import {
  OrderChildItem,
  OrderItem,
  OrderMethod,
  OrderProvider,
  calcDiscountTotal,
  calcLineItemCost,
  calcSubtotalAfterItemDiscounts,
  flattenChildItems,
  unravelToOneQuantity,
} from '@/libs/helpers/utils'
import { Divider, Grid, Text } from '@/tbui'
import { palette } from '@/tbui/DefaultThemeProvider/defaultTheme'
import { ItemDiscounts } from '../ItemDiscounts'

const StyledWrapper = styled(Grid)`
  border-radius: ${(props) => props.theme.shape.RADIUS_ALT};
  max-width: ${(props) => props.theme.breakpoints.XS};
  box-shadow: ${(props) => props.theme.shape.SHADOW};
  margin: 24px auto;
`

const QuantityWrapper = styled(Grid)`
  background-color: ${(props) => props.theme.palette.SECONDARY};
  border-radius: ${(props) => props.theme.shape.RADIUS};
  padding: 6px 8px;
  height: 25px;
`

const StyledText = styled(Text)`
  max-width: 90%;
`

const LineItemWrap = styled(Grid)`
  word-break: break-word;

  &:not(:last-of-type) {
    margin-bottom: 10px;
  }
`
const StyledTitleGrid = styled(Grid)<{ isBalanceDue?: boolean }>`
  ${({ isBalanceDue }) => isBalanceDue && 'margin-top: 10px;'}
`

const StyledFeeWaived = styled.span`
  text-decoration: line-through;
  margin-right: 2px;
`

const StyledDoordashDelivery = styled(Text)`
  margin-top: 10px;
`

const StyledLogo = styled.img`
  height: auto;
  width: 110px;
  margin-left: 5px;
  margin-top: 8px;
`

const advancedDiscountDetailStyles = css`
  color: ${palette.DISCOUNT};
`

const modifierClass = css`
  margin-bottom: 8px;
  margin-left: 8px;
`

const LineItem: FunctionComponent<{
  item: OrderItem
  itemIndex: number
  orderItemDiscounts: OrderItemDiscount[]
  children?: React.ReactNode
}> = ({ item, itemIndex, orderItemDiscounts }) => {
  const { t } = useTranslation()
  const { childItems } = item
  const itemSubtotal = calcLineItemCost(item)
  const discountTotal = calcDiscountTotal(orderItemDiscounts)
  const itemTotal = Big(itemSubtotal).sub(discountTotal).toFixed(2)

  const flattenedChildItems: (OrderChildItem & { depth: number })[] = useMemo(
    () =>
      childItems && childItems.length > 0
        ? flattenChildItems(unravelToOneQuantity(childItems), 0)
        : [],
    [childItems]
  )

  return (
    <LineItemWrap>
      <Grid template="auto 1fr auto" justify="space-around" align="center" gap="8px" m="8px 0">
        <QuantityWrapper p="4px" align="center" justify="center">
          <Text
            bold
            type="H4"
            data-test={`line-item-${item.itemID}-${itemIndex}-quantity`}
            color="TEXT_SECONDARY"
          >
            {Number(item.quantity).toFixed(0)}x
          </Text>
        </QuantityWrapper>
        <Text bold align="left" type="H4" data-test={`line-item-${item.itemID}-${itemIndex}-name`}>
          {item.name}
        </Text>
        <Text bold type="H4" data-test={`line-item-${item.itemID}-subtotal`}>
          {t('currency', {
            value: itemTotal,
          })}
        </Text>
      </Grid>
      {flattenedChildItems.map((childItem) => (
        <Text
          key={`${childItem.itemID}`}
          type="H5"
          className={modifierClass}
          data-test={`line-item-modifier-${childItem.itemID}`}
          style={{ paddingLeft: `${childItem.depth * 8}px`, fontSize: '14px' }}
        >
          {'+ '}
          {Big(childItem.price).gt(0) ? `${t('currency', { value: Big(childItem.price) })} ` : ''}
          {`${childItem.name}${
            childItem.quantity !== '1.00' ? ` (x${Number(childItem.quantity).toFixed(0)})` : ''
          }`}
        </Text>
      ))}
      {item.note && (
        <>
          <Text
            type="H5"
            className={modifierClass}
            data-test={`line-item-${item.itemID}-${itemIndex}-note`}
          >
            {`+ ${t('order.success.item_notes')} ${item.note}`}
          </Text>
        </>
      )}
      {orderItemDiscounts.length > 0 && (
        <ItemDiscounts
          index={itemIndex}
          itemTotal={itemSubtotal}
          orderItemDiscounts={orderItemDiscounts}
          className={modifierClass}
        />
      )}
    </LineItemWrap>
  )
}

const DataRow: FunctionComponent<{
  label: string
  value: string | ReactNode
  title?: boolean
  bold?: boolean
  isDiscount?: boolean
  dataTestID: string
}> = ({ label, value, title = false, bold, dataTestID, isDiscount }) => {
  const margin = title ? '16px 0' : '8px 0'
  const padding = '0 16px 0 0'
  const type = title ? 'P' : 'H5'

  return (
    <Grid template="auto auto" justify="space-between">
      <Text
        bold={bold}
        type={type}
        m={margin}
        p={padding}
        data-test={`${dataTestID}-label`}
        className={isDiscount ? advancedDiscountDetailStyles : undefined}
      >
        {label}
      </Text>
      <Text
        bold={bold}
        type={type}
        m={margin}
        data-test={`${dataTestID}-value`}
        className={isDiscount ? advancedDiscountDetailStyles : undefined}
      >
        {value}
      </Text>
    </Grid>
  )
}

export interface ReceiptProps {
  order: Order
  venue: Venue
  finalOrderLocation: {
    phone: string | null
    displayAddress?: string
    lat: number | null
    lng: number | null
  } // either pickup location with venue phone or delivery location with consumer phone
  hasAdvancedDiscount?: boolean
  discountDetails?: DiscountDetails
}

// eslint-disable-next-line complexity
const Receipt: FunctionComponent<ReceiptProps> = ({
  order,
  venue,
  finalOrderLocation,
  hasAdvancedDiscount,
  discountDetails,
}) => {
  const { t } = useTranslation()
  if (order == null) {
    return null
  }
  const translationContext = order.orderMethod.toLowerCase()
  const redeemedReward = order.loyalty?.redemptions[0]?.data
  const pointsCollected = order.loyalty?.earnings[0]?.creditAmount
  const pointsUsed = order.loyalty?.redemptions[0]?.debitAmount
  const pointsBalance = order.loyalty?.pointsBalance
  const isPaidWithGiftCard = order.bill.giftCardPayments != null
  const remainingCardBalance =
    order.bill.giftCardPayments != null ? order.bill.giftCardPayments[0].remainingBalance : null
  const giftCardLastFour = order.bill.giftCardPayments?.[0]?.lastFourDigits
  const giftCardLabel = giftCardLastFour
    ? t('bill.gift_card_masked', { lastFour: giftCardLastFour })
    : t('bill.gift_card')
  const hasBillDiscount =
    hasAdvancedDiscount && discountDetails ? discountDetails.billTotalDiscounts.length > 0 : false
  const subtotalAfterItemDiscounts = calcSubtotalAfterItemDiscounts(
    order.bill.subtotal,
    discountDetails ?? null,
    hasAdvancedDiscount && order.loyalty?.appliedLoyaltyOrderItemID
      ? (order.bill.discountAmount ?? null)
      : null
  )

  const isPaymentMethodOnline = order.payment.paymentMethod === PaymentMethod.ONLINE

  const isBalanceDue = !isPaymentMethodOnline && remainingCardBalance === '0.00'

  const isDelivery = order.orderMethod === OrderMethod.delivery

  const isDoordashDelivery =
    isDelivery && order.providerXRefID === OrderProvider.TOUCHBISTRO_DOORDASH

  const renderContactInfo = (): ReactNode => {
    return (
      <Text type="H5" m="4px 0 0 0">
        {finalOrderLocation && (
          <>
            {finalOrderLocation.displayAddress}
            <br />
          </>
        )}
        {formatPhone(finalOrderLocation.phone)}
      </Text>
    )
  }

  const renderOrderReadyTime = (): ReactNode => {
    if (order.scheduledFor != null) {
      const orderReadyTime = DateTime.fromISO(order.scheduledFor).toString()
      return (
        <Text type="H5" m="4px 0 0 0">
          {t('time', { value: orderReadyTime })}
          {' - '}
          {t('date', { value: orderReadyTime })}
        </Text>
      )
    }

    const { waitTime } = order
    if (isDoordashDelivery) {
      const deliveryTime = DateTime.fromISO(order.requestedAt).plus({
        minutes: order.waitTime,
      })
      return (
        <Text type="H5" m="4px 0 0 0">
          {t('date', { value: deliveryTime })}
          {t('order.success.at')}
          {t('time', { value: deliveryTime })}
        </Text>
      )
    }
    return (
      <Text type="H5" m="4px 0 0 0">
        {t('order.success.estimated_time', {
          waitTimeStart: waitTime,
          waitTimeEnd: waitTime + 10,
        })}
      </Text>
    )
  }

  const getPaymentMethodsWithAmounts = (giftCard?: string | null, value?: string): string =>
    `${
      giftCard ??
      t('order.success.payment_method', {
        context: order.payment.paymentMethod.toLowerCase(),
      })
    } (${t('currency', {
      value: value ?? order.bill.giftCardPayments?.[0].amountSpent,
    })})`

  const getPaymentMethod = (): string => {
    if (isPaidWithGiftCard) {
      if (remainingCardBalance !== '0.00') {
        return giftCardLabel
      }
      if (isPaymentMethodOnline) {
        return getPaymentMethodsWithAmounts(giftCardLabel)
      }
    }
    return t('order.success.payment_method', {
      context: order.payment.paymentMethod?.toLowerCase(),
    })
  }

  const getGrandTotalLabel = (isInclusive: boolean, currency?: string): string => {
    if (currency) {
      return t(isInclusive ? 'bill.total_currency_inclusive_taxes' : 'bill.total_currency', {
        currency,
      })
    }

    return t(isInclusive ? 'bill.total_inclusive_taxes' : 'bill.total')
  }

  const renderPaymentMethod = (): ReactNode => (
    <Grid gap="10px">
      <Text type="H5" transform="capitalize" align="right" data-test="payment-method-value">
        {getPaymentMethod()}
      </Text>
      {remainingCardBalance === '0.00' && isPaymentMethodOnline && (
        <Text type="H5" transform="capitalize" align="right">
          {getPaymentMethodsWithAmounts(null, order.bill.outstandingAmount)}
        </Text>
      )}
    </Grid>
  )

  const [addressFirstLine, ...addressRest] = venue.address.split(',')

  const renderBalanceDue = (): ReactNode =>
    !isPaymentMethodOnline &&
    remainingCardBalance === '0.00' && (
      <Grid>
        <DataRow
          value={t('currency_negative', {
            value: order.bill.giftCardPayments?.[0].amountSpent,
          })}
          label={giftCardLabel}
          dataTestID="line-item-remaining-balance-gift-card"
        />
        <DataRow
          value={t('currency', { value: order.bill.outstandingAmount })}
          label={t('order.success.balance_due')}
          dataTestID="line-item-remaining-balance-due"
          title
          bold
        />
      </Grid>
    )

  const renderRemainingGiftCardBalance = (): ReactNode =>
    remainingCardBalance && (
      <>
        <Grid p="16px 24px 16px 24px" gap="16px">
          <Grid data-test="card-balance" justify="space-between" template="auto auto">
            <Text type="H5">
              {giftCardLastFour
                ? t('order.success.remaining_card_balance_masked', { lastFour: giftCardLastFour })
                : t('order.success.remaining_card_balance')}
            </Text>
            <Text type="H5">
              {t('currency', {
                value: remainingCardBalance,
              })}
            </Text>
          </Grid>
        </Grid>{' '}
        <Divider />{' '}
      </>
    )

  const getBillDiscountElement = (): ReactNode[] => {
    if (discountDetails) {
      return discountDetails?.billTotalDiscounts.map((discount, index) => (
        <DataRow
          key={`line-discount-detail-${index}`}
          dataTestID={`line-discount-detail-${index}`}
          value={t('currency', { value: -Number(discount.total).toFixed(2) })}
          label={
            discount.applyType === 'PROMO'
              ? t('cart_widget.item.promo', { value: discount.label })
              : discount.label
          }
          isDiscount={hasAdvancedDiscount}
          bold
        />
      ))
    } else {
      return [<></>]
    }
  }

  return (
    <StyledWrapper>
      <Grid data-test="venue-details" p="24px" gap="8px">
        <Text type="H3" align="center" p="40px 0 0 0">
          {venue.name}
        </Text>
        <Text type="H5" align="center">
          {addressFirstLine}
          <br />
          {addressRest.join(',')}
          <br />
          {formatPhone(venue.phone)}
        </Text>
        <Text type="H5" m="24px 0 0 0" align="center">
          {t('date', { value: DateTime.fromISO(order.requestedAt) })}
          {t('order.success.at')}
          {t('time', { value: DateTime.fromISO(order.requestedAt) })}
          <br />
        </Text>
        <Text type="H5" align="center">
          {t('order.success.order_number', { value: order.orderNumber })}
        </Text>
      </Grid>
      <Divider />
      <Grid p="8px 24px" data-test="order-items">
        {order.items?.map((item, idx) => {
          let orderItemDiscounts: OrderItemDiscount[] = []
          if (discountDetails && discountDetails.orderItemDiscounts[item.id]) {
            orderItemDiscounts = [...discountDetails.orderItemDiscounts[item.id]]
          }
          if (
            redeemedReward &&
            order.loyalty?.appliedLoyaltyOrderItemID &&
            order.loyalty.appliedLoyaltyOrderItemID === item.id &&
            order.bill.discountAmount &&
            Big(order.bill.discountAmount).gt(0.0)
          ) {
            orderItemDiscounts.push({
              discountId: 'loyalty-reward',
              label: t('bill.loyalty_discount_amount', {
                name: redeemedReward?.name,
              }),
              total: order.bill.discountAmount ?? '0.00',
              applyType: 'MANUAL',
            })
          }
          return (
            <LineItem
              item={item}
              // fixing dupe key warning with index
              // eslint-disable-next-line react/no-array-index-key
              key={`${item.itemID}-${idx}`}
              itemIndex={idx}
              orderItemDiscounts={orderItemDiscounts}
            />
          )
        })}
      </Grid>
      <Divider />
      <Grid p="16px 24px 8px 24px" data-test="receipt-bill-breakdown">
        {order.isLegacy && redeemedReward && (
          <DataRow
            dataTestID="line-item-loyalty-reward"
            value={t('bill.loyalty_reward_discount', {
              value: t('currency', { value: order.bill.discountAmount }),
            })}
            label={t('bill.loyalty_reward', { value: redeemedReward.name })}
            isDiscount
            bold
          />
        )}
        <DataRow
          value={t('currency', { value: subtotalAfterItemDiscounts })}
          label={t('bill.item_subtotal')}
          dataTestID="line-item-subtotal"
        />
        {order.bill.serviceCharges
          // delivery fee is to be rendered first
          .sort((sc) => (sc.type === ServiceChargeType.DELIVERY_FEE ? -1 : 0))
          .filter((sc) => Number(sc.chargeAmount) > 0)
          .map((serviceCharge, index) => (
            // TODO: investigate this
            // pre-commit hook runs prettier again breaking this lint rule
            /* eslint-disable react/jsx-indent */
            // eslint-disable-next-line react/no-array-index-key
            <Grid template="1fr auto" key={`dlv-${index}`}>
              <DataRow
                dataTestID={`receipt-service-charge-${serviceCharge.serviceChargeXRefID}`}
                value={
                  <div data-test="receipt-delivery-fee-amount">
                    {serviceCharge.isWaived ? (
                      <>
                        <StyledFeeWaived
                          data-test={`receipt-service-charge-waived-${serviceCharge.serviceChargeXRefID}`}
                        >
                          {t('currency', {
                            // When the fee is inclusive include the fee taxes, when its not inclusive just show the delivery fee subtotal
                            value: order.bill.isInclusive
                              ? serviceCharge.totalAmount
                              : serviceCharge.chargeAmount,
                          })}
                        </StyledFeeWaived>
                        {t('currency', {
                          value: 0,
                        })}
                      </>
                    ) : (
                      t('currency', {
                        value: order.bill.isInclusive
                          ? serviceCharge.totalAmount
                          : serviceCharge.chargeAmount,
                      })
                    )}
                  </div>
                }
                label={`${
                  serviceCharge.type === ServiceChargeType.DELIVERY_FEE
                    ? t('bill.delivery_fee')
                    : serviceCharge.label
                }${serviceCharge.chargeType === ChargeType.PERCENTAGE ? ' (%)' : ''}`}
              />
            </Grid>
          ))}
        {!order.isLegacy && redeemedReward && !order.loyalty?.appliedLoyaltyOrderItemID && (
          <DataRow
            dataTestID="line-item-loyalty-reward"
            value={t('bill.loyalty_reward_discount', {
              value: t('currency', { value: order.bill.discountAmount }),
            })}
            label={t('bill.loyalty_reward', { value: redeemedReward.name })}
            isDiscount
            bold
          />
        )}
        {hasBillDiscount && getBillDiscountElement()}
        {!order.bill.isInclusive && (
          <DataRow
            dataTestID="line-item-total-tax"
            value={t('currency', { value: order.bill.totalTax })}
            label={t(order.bill.isEstimated ? 'bill.estimated_taxes' : 'bill.taxes')}
          />
        )}
        {order.bill.tipTotal && order.payment.paymentMethod === PaymentMethod.ONLINE && (
          <DataRow
            dataTestID="line-item-tip-total"
            value={t('currency', { value: order.bill.tipTotal })}
            label={t('bill.tip_amount')}
          />
        )}
        <StyledTitleGrid isBalanceDue={isBalanceDue}>
          <DataRow
            dataTestID="line-item-grand-total"
            value={t('currency', { value: order.bill.grandTotal })}
            label={getGrandTotalLabel(order.bill.isInclusive, order.payment.currency)}
            title={!isBalanceDue}
            bold={!isBalanceDue}
          />
        </StyledTitleGrid>
        {renderBalanceDue()}
      </Grid>
      <Divider />
      <Grid p="16px 24px 16px 24px" gap="16px">
        <Grid data-test="payment-method" justify="space-between" template="auto auto">
          <Text type="H5" data-test="payment-method-label">
            {t('order.success.payment_type')}
          </Text>
          {renderPaymentMethod()}
        </Grid>
      </Grid>
      <Divider />
      {renderRemainingGiftCardBalance()}
      {order.loyalty != null && (
        <>
          <Grid p="16px 24px 16px 24px" gap="16px">
            {pointsCollected && (
              <Grid data-test="loyalty-earnings" justify="space-between" template="auto auto">
                <Text type="H5">{t('order.success.points_earned')}</Text>
                <Text type="H5">{Math.trunc(Number(pointsCollected))}</Text>
              </Grid>
            )}
            {pointsUsed && (
              <Grid data-test="loyalty-redemptions" justify="space-between" template="auto auto">
                <Text type="H5">{t('order.success.points_used')}</Text>
                <Text type="H5">{Math.trunc(Number(pointsUsed))}</Text>
              </Grid>
            )}
            {pointsBalance && (
              <Grid data-test="loyalty-balance" justify="space-between" template="auto auto">
                <Text type="H5">{t('order.success.points_balance')}</Text>
                <Text type="H5">{Math.trunc(Number(pointsBalance))}</Text>
                <Text type="H5" m="10px 0" fontSize="11px" lineHeight="16px">
                  {t('order.success.points_information')}
                </Text>
              </Grid>
            )}
          </Grid>
          <Divider />
        </>
      )}
      {finalOrderLocation.lat != null && finalOrderLocation.lng != null && (
        <Grid p="16px" data-test="food-end-location-details">
          <Grid p="8px">
            <Text bold>
              {t('order.success.details', {
                context: translationContext,
              })}
            </Text>
            {isDoordashDelivery && (
              <StyledDoordashDelivery>
                {t('order.success.delivered_by_doordash')}
                <StyledLogo src={buildAssetsPath('/images/doordash_logo.png')} alt="Doordash" />
              </StyledDoordashDelivery>
            )}
          </Grid>
          <Grid p="8px">
            <Grid template="auto 1fr" gap="10px">
              <Map latitude={finalOrderLocation.lat} longitude={finalOrderLocation.lng} small />
              <Grid>
                <Text bold>
                  {t('order.success.info.title', {
                    context: translationContext,
                  })}
                </Text>
                {renderContactInfo()}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
      <Grid p="16px 24px 24px 24px" data-test="order-time">
        <Text bold>
          {t('order.success.time', {
            context: translationContext,
          })}
        </Text>
        {renderOrderReadyTime()}
        {!isDoordashDelivery && (
          <StyledText m="8px 0" color="TEXT_2" type="H5">
            {t('order.success.time_disclaimer', {
              context: translationContext,
            })}
          </StyledText>
        )}
        {!!order.notes && (
          <Grid m="16px 0 0 0">
            <Text bold>{t('order.success.notes', { context: translationContext })}</Text>
            <Text type="H5" m="4px 0 0 0">
              {order.notes}
            </Text>
          </Grid>
        )}
      </Grid>
      <Divider />
      <Text type="H5" align="center" m="24px">
        {t('order.success.footer')}
      </Text>
    </StyledWrapper>
  )
}

export default Receipt
